import { WIDGET_GRID_HEIGHT } from '@/constants/grid';
import { minimalScrollbarStyling } from '@/constants/styles';
import { useFillHeight } from '@/hooks/useFillHeight';
import { Box } from '@mui/material';
import { PropsWithChildren, useRef } from 'react';

export type GridContainerSize = 'sm' | 'md' | 'lg' | 'flyout' | 'auto' | 'fill';

/** Height cap of a `fill` container on a short screen, so several rows show. */
export const FILL_GRID_MIN_HEIGHT = 320;

const SIZE_HEIGHTS: Record<Exclude<GridContainerSize, 'fill'>, string> = {
  sm: '50vh',
  md: '65vh',
  lg: '80vh',
  flyout: '400px',
  auto: '100%',
};

interface GridContainerProps {
  isWidget: boolean;
  /**
   * `fill` grows with the rows up to the space left in the scroll area, then
   * the grid scrolls inside it. The cap follows the window. `fill` takes
   * precedence over `isWidget`.
   */
  size?: GridContainerSize;
}

export const GridContainer = ({
  isWidget,
  children,
  size = 'md',
}: PropsWithChildren<GridContainerProps>) => {
  const ref = useRef<HTMLDivElement>(null);
  const isFill = size === 'fill';
  const fillHeight = useFillHeight(ref, FILL_GRID_MIN_HEIGHT, isFill);

  const sizing = isFill
    ? {
        display: 'flex',
        flexDirection: 'column' as const,
        minHeight: 0,
        maxHeight: fillHeight ?? FILL_GRID_MIN_HEIGHT,
      }
    : { height: isWidget ? WIDGET_GRID_HEIGHT : SIZE_HEIGHTS[size] };

  return (
    <Box
      ref={ref}
      sx={{
        ...minimalScrollbarStyling,
        overflow: 'auto',
        width: '100%',
        ...sizing,
      }}
    >
      {children}
    </Box>
  );
};
